// The Analytics tab: Giraffe's own analytics measures, read live via
// `getAnalyticsResult()` and laid out as rows (one per measure) by columns
// (one per group key — the scenario/usage split Giraffe's own analytics
// panel shows as columns).
//
// Layout: Tab | Category | Measure | Total | <split columns…> | Sum | Check.
//
// Every SPLIT cell is the value `getAnalyticsResult()` already computed —
// not a `GI.SUM`/`GI.WHERE` formula reconstructed from a measure's own
// `formula`/`components`. An earlier version of this file did reconstruct
// formulas that way, guessing at `getAnalyticsResult()`'s shape because no
// tool available could confirm it; that guess is gone now that the real
// shape is confirmed (giraffe/analyticsResult.ts) — and reading the value
// directly is not just simpler, it is MORE correct: it handles a `subtotal`
// or `site` component exactly as well as a plain one, with no reconstruction
// to get right or wrong, because Giraffe already did the computing.
//
// Total, Sum and Check are the one place this sheet DOES reconstruct a
// formula, and on purpose: Total is gTables' OWN `GI.SUMEACH`/`GI.WHERE`
// formula over the Features sheet (nativeMeasures.ts's `totalFormulaOf`),
// built independently of Giraffe's analytics engine; Sum adds the split
// columns together; Check compares the two. Agreement is the point — it is
// gTables checking its own read of the project against Giraffe's own
// computed total, not the other way round. Only a Property-only `SUM`
// measure gets one; see `totalFormulaOf`'s own comment for why.

import type { Sheet, Workbook } from '@/model/types';
import { naturalCompare } from '@/model/collate';
import { createSheet, sheetByRole, setCellValue, newId, uniqueSheetName } from '@/model/workbook';
import { rowColToA1 } from '@/model/address';
import { HEADER_ROW, FIRST_DATA_ROW } from './entityTable';
import { analyticsRows, columnKey, columnLabel, type AnalyticsDataRoot, type AnalyticsRow } from './analyticsTypes';
import { indexOfOrBig, sortByAnalyticsOrder, totalFormulaOf } from '@/giraffe/nativeMeasures';
import type { AnalyticsLayout } from '@/giraffe/analyticsMeasures';

export const ANALYTICS_SHEET_NAME = 'Analytics';

const COL_TAB = 1;
const COL_CATEGORY = 2;
const COL_MEASURE = 3;
const COL_TOTAL = 4;
const FIRST_SPLIT_COL = 5;

export function ensureAnalyticsSheet(wb: Workbook): Sheet {
  let sheet = sheetByRole(wb, 'analytics');
  if (!sheet) {
    sheet = createSheet(uniqueSheetName(wb, ANALYTICS_SHEET_NAME), newId('analytics'));
    sheet.role = 'analytics';
    wb.sheets.push(sheet);
  }
  return sheet;
}

function bold(sheet: Sheet, row: number, col: number): void {
  const key = `${row},${col}`;
  sheet.cells[key] = { ...sheet.cells[key], s: { ...sheet.cells[key]?.s, bold: true } };
}

/** Whether `data` has anything to show — the gate `store.ts` uses to decide whether the sheet should exist at all. */
export function hasAnalyticsMeasures(data: AnalyticsDataRoot | null): boolean {
  return analyticsRows(data).some((r) => r.measure?.id);
}

/** `counter.tabs[id]?.label`, or the raw id when Giraffe never named it. */
function tabLabelOf(tabId: string | undefined, layout: AnalyticsLayout | null): string {
  if (!tabId) return '';
  return layout?.tabs[tabId]?.label ?? tabId;
}

/**
 * Rewrite the Analytics sheet for the current `getAnalyticsResult()`
 * snapshot. Blank-and-rebuild, like Measures used to be — nothing here is
 * ever typed into. `layout` and `knownFields` are optional: without them
 * (data read live but the project's `counter` app not yet broadcast, or a
 * test exercising split-column values only) Tab/Category/Total go blank and
 * rows keep `getAnalyticsResult()`'s own order, rather than the sheet
 * refusing to render at all.
 */
export function writeAnalyticsSheet(
  sheet: Sheet,
  data: AnalyticsDataRoot | null,
  layout: AnalyticsLayout | null = null,
  knownFields: ReadonlySet<string> = new Set()
): void {
  sheet.cells = {};
  const headers: [number, string][] = [
    [COL_TAB, 'Tab'],
    [COL_CATEGORY, 'Category'],
    [COL_MEASURE, 'Measure'],
    [COL_TOTAL, 'Total'],
  ];
  for (const [col, text] of headers) {
    setCellValue(sheet, HEADER_ROW, col, text);
    bold(sheet, HEADER_ROW, col);
  }

  // (row, tab/category label) — `data.grouped`'s own keys ARE categoryIds
  // (confirmed against Cashflow's `indexCategories`), and that is the only
  // reliable source for one: `row.measure.categoryId` is the REDUCED shape
  // `getAnalyticsResult()` hands back, and is not always populated on every
  // row.
  interface Placed {
    row: AnalyticsRow;
    tabLabel: string;
    categoryLabel: string;
  }
  const placed: Placed[] = [];
  for (const [categoryId, group] of Object.entries(data?.grouped ?? {})) {
    const tabLabel = tabLabelOf(group.tabs?.[0], layout);
    const categoryLabel = group.label ?? categoryId;
    for (const row of group.usages?.__COMBINED?.rows ?? []) {
      if (!row.measure?.id) continue;
      placed.push({ row, tabLabel, categoryLabel });
    }
  }
  if (placed.length === 0) {
    sheet.rows = Math.max(sheet.rows, FIRST_DATA_ROW);
    sheet.cols = Math.max(sheet.cols, COL_TOTAL);
    return;
  }

  // Analytics' own display order — see `sortByAnalyticsOrder`'s doc comment.
  const rank = new Map(
    sortByAnalyticsOrder(layout ? [...layout.measuresById.values()] : [], layout).map((m, i) => [m.id, i])
  );
  const sorted = [...placed].sort(
    (a, b) =>
      (rank.get(a.row.measure!.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.row.measure!.id) ?? Number.MAX_SAFE_INTEGER)
  );

  // Distinct split columns, in Analytics' own `display.columnOrder` (by
  // LABEL, not groupKey — confirmed live), first-seen order for the rest.
  const colOrder: string[] = [];
  const colLabelOf = new Map<string, string>();
  for (const { row } of sorted) {
    for (const col of row.columns ?? []) {
      const key = columnKey(col);
      if (!colLabelOf.has(key)) {
        colLabelOf.set(key, columnLabel(col));
        colOrder.push(key);
      }
    }
  }
  const columnOrder = layout?.display.columnOrder;
  colOrder.sort((a, b) => {
    const diff = indexOfOrBig(columnOrder, colLabelOf.get(a)) - indexOfOrBig(columnOrder, colLabelOf.get(b));
    if (diff !== 0) return diff;
    return naturalCompare(colLabelOf.get(a)!, colLabelOf.get(b)!);
  });
  colOrder.forEach((key, i) => {
    const c = FIRST_SPLIT_COL + i;
    setCellValue(sheet, HEADER_ROW, c, colLabelOf.get(key)!);
    bold(sheet, HEADER_ROW, c);
  });
  const colIndex = new Map(colOrder.map((key, i) => [key, FIRST_SPLIT_COL + i]));
  const sumCol = FIRST_SPLIT_COL + colOrder.length;
  const checkCol = sumCol + 1;
  setCellValue(sheet, HEADER_ROW, sumCol, 'Sum');
  bold(sheet, HEADER_ROW, sumCol);
  setCellValue(sheet, HEADER_ROW, checkCol, 'Check');
  bold(sheet, HEADER_ROW, checkCol);

  sorted.forEach(({ row, tabLabel, categoryLabel }, i) => {
    const r = FIRST_DATA_ROW + i;
    const measure = row.measure!;
    setCellValue(sheet, r, COL_TAB, tabLabel);
    setCellValue(sheet, r, COL_CATEGORY, categoryLabel);
    setCellValue(sheet, r, COL_MEASURE, measure.name);

    const full = layout?.measuresById.get(measure.id);
    const total = full ? totalFormulaOf(full, knownFields) : undefined;
    if (total !== undefined) sheet.cells[`${r},${COL_TOTAL}`] = { f: total };

    for (const col of row.columns ?? []) {
      const c = colIndex.get(columnKey(col));
      if (c === undefined || col.value === undefined || col.value === null) continue;
      setCellValue(sheet, r, c, col.value);
    }

    if (colOrder.length > 0) {
      const first = rowColToA1(r, FIRST_SPLIT_COL);
      const last = rowColToA1(r, sumCol - 1);
      sheet.cells[`${r},${sumCol}`] = { f: `SUM(${first}:${last})` };
      const totalRef = rowColToA1(r, COL_TOTAL);
      const sumRef = rowColToA1(r, sumCol);
      sheet.cells[`${r},${checkCol}`] = {
        f: `IF(${totalRef}="","n/a",IF(ABS(${sumRef}-${totalRef})<=0.001*MAX(ABS(${sumRef}),ABS(${totalRef}),1),"OK","MISMATCH"))`,
      };
    }
  });

  sheet.rows = Math.max(sheet.rows, FIRST_DATA_ROW + sorted.length);
  sheet.cols = Math.max(sheet.cols, checkCol);
}
